// src/features/public/PublicHomePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import PoweredByAkuto from '../../components/PoweredByAkuto';
import { doc, onSnapshot, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import {
  setResumeShopAfterAuth,
  saveCheckoutIntent,
} from "../../lib/publicShopSession";
import PublicBookingModal from "./PublicBookingModal";

import {
  Sparkles,
  Globe,
  QrCode,
  ShoppingBag,
  CalendarDays,
  ChevronDown,
} from "lucide-react";

import { db, tCollection } from "../../firebase";
import { TENANT_ID } from "../../tenant";
import ShopModalLocal from "../dashboard/components/ShopModalLocal";
import PublicGenderPickerModal from "./PublicGenderPickerModal";

const FALLBACK_APP_CONFIG = {
  app_title: "suomi",
  catch_copy: "",
  header_color: "#0B1220",
  logo_url: "",
  website_url: "",
};

function SimpleActionCard({
  icon,
  title,
  description,
  buttonLabel,
  onClick,
  disabled = false,
}) {
  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 pb-6">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-3xl bg-gray-100 flex items-center justify-center text-gray-600 shrink-0">
          {icon}
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-[15px] font-black text-gray-900">{title}</h3>
          <p className="mt-1 text-sm text-gray-500 leading-relaxed">
            {description}
          </p>
        </div>
      </div>

      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className="mt-4 w-full bg-white text-gray-900 border border-gray-200 hover:bg-gray-50 shadow-sm transition-all font-bold py-4 rounded-3xl flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span>{buttonLabel}</span>
      </button>
    </div>
  );
}

function PurchasePreviewBlock({ onClick }) {
  return (
    <div className="bg-white rounded-3xl p-4 border border-gray-100 shadow-lg shadow-black/5 space-y-1.5 font-sans">
      <div className="flex items-center gap-1 px-0.5 text-gray-900">
        <div className="w-6 h-6 flex items-center justify-center">
          <ShoppingBag size={15} />
        </div>
        <p className="text-[10px] text-gray-500 font-black uppercase tracking-[0.1em]">
          チケット・グッズ・プラン
        </p>
      </div>

      <button
        type="button"
        onClick={onClick}
        className="w-full py-3.5 rounded-3xl flex items-center justify-center gap-2 font-black text-base active:scale-[0.97] transition-all bg-accent text-white shadow-sm"
      >
        <ShoppingBag size={20} />
        購入する
      </button>

      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0 bg-gray-50/30 rounded-xl px-5 py-3 border border-gray-100 flex items-center justify-between">
          <div className="text-[13px] font-black tracking-tighter truncate text-gray-500">
            購入後、確認やギフトができます
          </div>
          <ChevronDown size={20} className="text-gray-300" />
        </div>
      </div>
    </div>
  );
}

function BookingPreviewBlock({ onClick }) {
  return (
    <div className="bg-white rounded-3xl p-4 border border-gray-100 shadow-lg shadow-black/5 space-y-1.5 font-sans">
      <div className="flex items-center gap-1 px-0.5 text-gray-900">
        <div className="w-6 h-6 flex items-center justify-center">
          <CalendarDays size={15} />
        </div>
        <p className="text-[10px] text-gray-500 font-black uppercase tracking-[0.1em]">
          無料体験・会議室・イベント予約
        </p>
      </div>

      <button
        type="button"
        onClick={onClick}
        className="w-full py-3.5 rounded-3xl flex items-center justify-center gap-2 font-black text-base active:scale-[0.97] transition-all bg-gray-900 text-white shadow-lg shadow-gray-200"
      >
        <CalendarDays size={20} />
        予約する
      </button>

      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0 bg-gray-50/30 rounded-xl px-5 py-3 border border-gray-100 flex items-center justify-between">
          <div className="text-[13px] font-black tracking-tighter truncate text-gray-500">
            予約後、確認や招待ができます
          </div>
          <ChevronDown size={20} className="text-gray-300" />
        </div>
      </div>
    </div>
  );
}

function buildCatalogFromProductsSnap(productsSnap) {
  const all = { ticket: [], goods: [], plan: [] };

  productsSnap.forEach((d) => {
    const data = d.data() || {};
    const type = data?.metadata?.product_type || "ticket";
    const normalizedType = ["ticket", "goods", "plan"].includes(type)
      ? type
      : "ticket";

    const unitAmount = Number(data?.unit_amount ?? 0) || 0;
    const currency = String(data?.currency || "jpy");

    const priceId =
      String(data?.active_price_id || "").trim() ||
      String(data?.default_price || "").trim() ||
      "";

    const activePrice = {
      id: priceId,
      unit_amount: unitAmount,
      currency,
      active: true,
    };

    all[normalizedType].push({
      id: d.id,
      ...data,
      type: normalizedType,
      price: activePrice,
    });
  });

  ["ticket", "goods", "plan"].forEach((t) => {
    all[t].sort(
      (a, b) =>
        parseInt(a?.metadata?.display_order || 999, 10) -
        parseInt(b?.metadata?.display_order || 999, 10)
    );
  });

  return all;
}

export default function PublicHomePage({
  onOpenAuth,
  onOpenShop,
  onOpenBooking,
}) {
  const navigate = useNavigate();
  const [appConfig, setAppConfig] = useState(FALLBACK_APP_CONFIG);

  const [showShop, setShowShop] = useState(false);
  const [showPublicBooking, setShowPublicBooking] = useState(false);
  const [showGenderPicker, setShowGenderPicker] = useState(false);
  const [publicGender, setPublicGender] = useState("");

  const [publicServices, setPublicServices] = useState([]);
  const [publicServicesReady, setPublicServicesReady] = useState(false);

  const [shopCatalog, setShopCatalog] = useState({
    ticket: [],
    goods: [],
    plan: [],
  });
  const [shopCatalogReady, setShopCatalogReady] = useState(false);

  useEffect(() => {
    const ref = doc(db, "tenants", TENANT_ID, "config", "app");

    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data() || {};
        setAppConfig((prev) => ({ ...prev, ...data }));
      },
      (e) => {
        console.error("[PublicHomePage] app_config onSnapshot error:", e);
      }
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    const q = query(
      tCollection("products", TENANT_ID),
      where("active", "==", true)
    );

    setShopCatalogReady(false);

    const unsub = onSnapshot(
      q,
      { includeMetadataChanges: true },
      (snap) => {
        const all = buildCatalogFromProductsSnap(snap);
        setShopCatalog(all);
        setShopCatalogReady(true);
      },
      (e) => {
        console.error("[PublicHomePage] products onSnapshot error:", e);
        setShopCatalog({ ticket: [], goods: [], plan: [] });
        setShopCatalogReady(true);
      }
    );

    return () => unsub();
  }, []);

  useEffect(() => {
    const q = query(
      tCollection("services", TENANT_ID),
      where("is_active", "==", true)
    );

    setPublicServicesReady(false);

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setPublicServices(rows);
        setPublicServicesReady(true);
      },
      (e) => {
        console.error("[PublicHomePage] services onSnapshot error:", e);
        setPublicServices([]);
        setPublicServicesReady(true);
      }
    );

    return () => unsub();
  }, []);

  const headerColor = String(appConfig?.header_color || "#0B1220");
  const logoUrl = String(appConfig?.logo_url || "").trim();
  const websiteUrl = String(appConfig?.website_url || "").trim();

  const showWebsiteLink = useMemo(() => {
    try {
      const u = new URL(websiteUrl);
      return u.protocol === "http:" || u.protocol === "https:";
    } catch {
      return false;
    }
  }, [websiteUrl]);

  const openAuth = () => {
    if (typeof onOpenAuth === "function") {
      onOpenAuth();
      return;
    }
    navigate("/auth/start");
  };

  const openShop = () => {
    if (typeof onOpenShop === "function") {
      onOpenShop();
      return;
    }
    setShowGenderPicker(true);
  };

  const handleSelectPublicGender = (value) => {
    setPublicGender(value);
    setShowGenderPicker(false);
    setShowShop(true);
  };

  const closeShop = () => {
    setShowShop(false);
    setPublicGender("");
  };

  const closePublicBooking = () => {
    setShowPublicBooking(false);
  };

  const handleProceedBookingAuth = (bookingDraft) => {
    setResumeShopAfterAuth(true);
    saveCheckoutIntent({
      type: "OPEN_BOOKING",
      bookingDraft: bookingDraft || null,
    });

    setShowPublicBooking(false);

    navigate("/auth/start", {
      state: {
        redirectTo: "/",
      },
    });
  };

  const closeGenderPicker = () => {
    setShowGenderPicker(false);
    setPublicGender("");
  };

  const openBooking = () => {
    if (typeof onOpenBooking === "function") {
      onOpenBooking();
      return;
    }
    setShowPublicBooking(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans pb-24 text-gray-900 relative overflow-x-hidden">
      <div
        className="fixed top-0 left-0 right-0 pointer-events-none"
        style={{
          height: "calc(env(safe-area-inset-top) + 6px)",
          background: "rgba(37, 37, 37, 1)",
          zIndex: 30,
        }}
      />

      <header
        className="pt-8 pb-16 px-6 rounded-b-[2.25rem] relative overflow-hidden shadow-xl"
        style={{ backgroundColor: headerColor }}
      >

        <div className="flex justify-between items-center max-w-[380px] mx-auto relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-white/10 rounded-xl flex items-center justify-center backdrop-blur-md border border-white/20 overflow-hidden">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={appConfig?.app_title || "logo"}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              ) : (
                <Sparkles
                  className="text-white"
                  size={18}
                  strokeWidth={2.5}
                />
              )}
            </div>

            <div className="flex flex-col">
              <h1 className="font-black text-lg leading-tight text-white tracking-tight">
                {appConfig?.app_title || "suomi"}
              </h1>
              {!!appConfig?.catch_copy && (
                <p className="text-[11px] text-white/70 font-medium leading-tight mt-0.5">
                  {appConfig.catch_copy}
                </p>
              )}
            </div>
          </div>

          {showWebsiteLink ? (
            <a
              href={websiteUrl}
              target="_blank"
              rel="noreferrer"
              className="bg-white/5 p-2 rounded-xl text-white/80 hover:bg-white/10 transition-colors"
              aria-label="公式サイトを開く"
              title="公式サイトを開く"
            >
              <Globe size={20} />
            </a>
          ) : (
            <div className="w-10" />
          )}
        </div>
      </header>

      <div className="px-5 -mt-12 space-y-3 max-w-[380px] mx-auto relative z-20">
        <SimpleActionCard
          icon={<QrCode size={38} />}
          title="入館用QR"
          description="QRはログイン後に表示されます。"
          buttonLabel="ログイン"
          onClick={openAuth}
        />

        <PurchasePreviewBlock onClick={openShop} />

        <BookingPreviewBlock onClick={openBooking} />

      </div>

      {showShop && (
        <ShopModalLocal
          isOpen={showShop}
          onClose={closeShop}
          userProfile={null}
          preloadedProductsByType={shopCatalog}
          preloadedReady={shopCatalogReady}
          publicView={true}
          publicGender={publicGender}
        />
      )}

      <PublicGenderPickerModal
        open={showGenderPicker}
        onClose={closeGenderPicker}
        onSelect={handleSelectPublicGender}
      />

      <PublicBookingModal
        isOpen={showPublicBooking}
        onClose={closePublicBooking}
        services={publicServices}
        isLoading={!publicServicesReady}
        onProceedAuth={handleProceedBookingAuth}
      />
      <PoweredByAkuto />
    </div>
  );
}